import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportBranchComparisonReport,
  fetchBranchComparisonReport,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

export function BranchComparisonReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
    }),
    [period, customFrom, customTo, bounds],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchBranchComparisonReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load branch comparison report.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportBranchComparisonReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to export report.'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="card-surface space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Branch comparison</h2>
          <p className="text-sm text-muted-foreground">
            Sales, appointments, and commission across branches with HQ FX totals.
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" disabled={exporting} onClick={() => handleExport('csv')}>
            CSV
          </Button>
          <Button type="button" size="sm" variant="outline" disabled={exporting} onClick={() => handleExport('pdf')}>
            PDF
          </Button>
        </div>
      </div>

      <ReportFilterBar
        period={period}
        compare={compare}
        onPeriodChange={setPeriod}
        onCompareChange={setCompare}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
      />

      {error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading branch comparison…
        </div>
      ) : null}

      {!loading && report ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Branches" value={String(report.summary?.branch_count ?? 0)} />
            <StatCard
              label={`HQ gross (${report.hq_currency})`}
              value={report.summary?.total_hq_gross ?? '—'}
            />
            <StatCard label="Appointments" value={String(report.summary?.total_appointment_count ?? 0)} />
            <StatCard
              label={`HQ commission (${report.hq_currency})`}
              value={report.summary?.total_hq_commission ?? '—'}
            />
          </div>

          {report.multi_currency_footnote ? (
            <p className="text-xs text-muted-foreground">{report.multi_currency_footnote}</p>
          ) : null}

          <div className="overflow-x-auto rounded-xl border border-border/60">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Branch</th>
                  <th className="px-3 py-2">Sales</th>
                  <th className="px-3 py-2">Gross</th>
                  <th className="px-3 py-2">HQ gross</th>
                  <th className="px-3 py-2">Appts</th>
                  <th className="px-3 py-2">Commission</th>
                  <th className="px-3 py-2">HQ commission</th>
                </tr>
              </thead>
              <tbody>
                {(report.branches ?? []).map((row) => (
                  <tr key={row.branch_id} className="border-t border-border/50">
                    <td className="px-3 py-2 font-medium">{row.branch_name}</td>
                    <td className="px-3 py-2 tabular-nums">{row.sale_count}</td>
                    <td className="px-3 py-2 tabular-nums">
                      {row.currency_code} {formatReportMoney(row.gross_total)}
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      {row.hq_gross_total ? `${report.hq_currency} ${formatReportMoney(row.hq_gross_total)}` : '—'}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{row.appointment_count}</td>
                    <td className="px-3 py-2 tabular-nums">
                      {row.currency_code} {formatReportMoney(row.commission_total)}
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      {row.hq_commission_total
                        ? `${report.hq_currency} ${formatReportMoney(row.hq_commission_total)}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function StatCard({ label, value }) {
  return (
    <div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
      <p className="text-[10px] uppercase text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
    </div>
  )
}
